import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck, FaGlobe } from 'react-icons/fa';
import { useI18n, useT } from '../i18n';
import './LanguageSwitcher.css';

/**
 * The header control for choosing a language.
 *
 * Languages are listed in their own language: someone looking for Japanese
 * scans for 日本語, not for "Japanese". A menu rather than a dialog, for the
 * same reason as ThemeSwitcher — small and transient, so it deliberately does
 * not take a body scroll lock.
 */
function LanguageSwitcher() {
  const { lang, setLanguage, languages } = useI18n();
  const t = useT();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);

  const active = languages.find((l) => l.code === lang) || languages[0];
  const activeIndex = languages.findIndex((l) => l.code === lang);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  // Focus the active row when the menu opens, so the keyboard lands somewhere
  // meaningful rather than on the first item every time.
  useEffect(() => {
    if (!open) return;
    const target = itemRefs.current[activeIndex >= 0 ? activeIndex : 0];
    if (target) target.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const from = itemRefs.current.indexOf(document.activeElement);
    const next = (from + step + languages.length) % languages.length;
    itemRefs.current[next]?.focus();
  };

  const choose = (code) => {
    setLanguage(code);
    close();
  };

  return (
    <div className="language-switcher" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className="language-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('a11y.language', { label: active.nativeName })}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <FaGlobe aria-hidden="true" />
        <span className="language-code">{active.code.slice(0, 2).toUpperCase()}</span>
      </button>

      {open && (
        <div className="language-menu" role="menu" aria-label={t('language.label')} onKeyDown={onMenuKeyDown}>
          <p className="language-menu-title">{t('language.label')}</p>
          {languages.map((language, index) => (
            <button
              key={language.code}
              type="button"
              role="menuitem"
              aria-current={language.code === lang ? 'true' : undefined}
              ref={(node) => { itemRefs.current[index] = node; }}
              className={`language-item ${language.code === lang ? 'is-active' : ''}`}
              lang={language.code}
              onClick={() => choose(language.code)}
            >
              <span className="language-native">{language.nativeName}</span>
              <span className="language-english">{language.englishName}</span>
              {language.code === lang && <FaCheck className="language-check" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
